/**
 * MiniMap — dashboard overview map.
 *
 * Rewritten to use MapboxMiniMap instead of Leaflet.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import MapboxMiniMap from '../maps/MapboxMiniMap';

interface MapDataProps {
    cohorts: any[];
    warehouses: any[];
    farmers?: any[];
}

const MiniMap = ({ cohorts = [], warehouses = [], farmers = [] }: MapDataProps) => {
    const navigate = useNavigate();

    // Filter valid data
    const validCohorts = cohorts.filter(c =>
        c.boundary_coordinates && Array.isArray(c.boundary_coordinates) && c.boundary_coordinates.length >= 3
    );
    const validWarehouses = warehouses.filter(w => w.location_lat && w.location_lng);
    const validFarmers = farmers.filter(f =>
        (f.plot_boundary && Array.isArray(f.plot_boundary) && f.plot_boundary.length >= 3) ||
        (f.location_coordinates && !isNaN(f.location_coordinates?.lat) && !isNaN(f.location_coordinates?.lng))
    );

    const hasData = validCohorts.length > 0 || validWarehouses.length > 0 || validFarmers.length > 0;

    // Build polygons and markers for MapboxMiniMap
    const polygons = [
        ...validCohorts.map(c => ({
            id: `cohort-${c.id}`,
            coordinates: c.boundary_coordinates.map((coord: any) => [Number(coord.lat), Number(coord.lng)] as [number, number]),
            color: '#10B981',
            label: c.name || '',
            onClick: () => navigate('/dashboard/maps'),
        })),
        ...validFarmers
            .filter(f => f.plot_boundary && Array.isArray(f.plot_boundary) && f.plot_boundary.length >= 3)
            .map(f => ({
                id: `farmer-${f.id}`,
                coordinates: f.plot_boundary.map((coord: any) => [Number(coord.lat), Number(coord.lng)] as [number, number]),
                color: '#F59E0B',
                label: f.full_name || '',
                onClick: () => navigate(`/dashboard/farmers/${f.id}`),
            })),
    ];

    const markers = [
        ...validWarehouses.map(w => ({
            id: `warehouse-${w.id}`,
            position: [w.location_lat, w.location_lng] as [number, number],
            color: '#7C3AED',
            label: w.name || '',
        })),
        ...validFarmers
            .filter(f => !f.plot_boundary && f.location_coordinates)
            .map(f => ({
                id: `farmer-loc-${f.id}`,
                position: [f.location_coordinates.lat, f.location_coordinates.lng] as [number, number],
                color: '#F59E0B',
                label: f.full_name || '',
                onClick: () => navigate(`/dashboard/farmers/${f.id}`),
            })),
    ];

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800">Geospatial Overview</h3>
                <button
                    onClick={() => navigate('/dashboard/maps')}
                    className="text-sm text-blue-600 font-medium hover:underline"
                >
                    Expand Map
                </button>
            </div>

            <div className="flex-1 min-h-[300px] rounded-xl overflow-hidden relative z-0">
                {!hasData && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
                        <div className="text-center">
                            <GlobeAltIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">No map data available</p>
                            <p className="text-xs text-slate-400 mt-1">Add farmers, field boundaries, or warehouses with GPS coordinates</p>
                        </div>
                    </div>
                )}
                <MapboxMiniMap
                    center={[-1.9441, 30.0619]}
                    zoom={8}
                    basemap="streets"
                    polygons={polygons}
                    markers={markers}
                    fitBounds={hasData}
                    scrollWheelZoom={false}
                />
            </div>
        </div>
    );
};

export default MiniMap;
